import wrapHttpPromise from './http.promise.js';

export const name = 'rnHttp';
/**
 * @ngdoc service
 * @name rhino.core.http.service:rnHttp
 * @description Proxy for $http calls that reference services from `rnRegistry`.
 * This service will resolve the registry data in a central service, keeping rhino
 * code from having to wrap an extra promise.
 *
 * This service returns promises wrapped with the now deprecated `success` and `error` methods.
 * It is recommended to use the standard promise `then` and `catch` methods instead, which
 * return a response object.
 *
 * @example
 * Get preferences from the portal service,
 * ```js
 * rnHttp.get('portal_service', 'preferences')
 *  .then(response => {})
 *  .catch(response => {});
 * ```
 */
export default function rnHttp(rnRegistry, $http, $log, $q) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  let service = {
    simpleRequest: simpleRequest,
    request: request,
    halRequest: halRequest,
    get: createShortcut('GET'),
    post: createShortcutWithContent('POST'),
    put: createShortcutWithContent('PUT'),
    patch: createShortcutWithContent('PATCH'),
    delete: createShortcut('DELETE'),
    // PrintOS has some deletes with content.
    // However, $http doesn't have data on their API.
    // Creating a separate method to keep a faithful proxy.
    deleteWithBody: createShortcutWithContent('DELETE'),
    head: createShortcut('HEAD'),
    resolveWithData: resolveWithData,
    rejectWithSmsError: rejectWithSmsError,
  };

  return service;

  ////////////////

  /**
   * @ngdoc function
   * @name simpleRequest
   * @methodOf rhino.core.http.service:rnHttp
   * @description Proxy for base $http function.
   * @param {IRequestConfig} config $http config object
   * @returns {IHttpPromise<T>} proxy for angular $http request promise
   *  with **deprecated** `success` and `error` convenience functions.
   * @see https://docs.angularjs.org/api/ng/service/$http
   */
  function simpleRequest(config = {}) {
    return wrapHttpPromise($http(config));
  }

  /**
   * @ngdoc function
   * @methodOf rhino.core.http.service:rnHttp
   * @description Proxy for base $http function.
   * @param {string} serviceName name of the service to make a request against
   * @param {string} path service relative request path
   * @param {IRequestConfig} config $http config object
   * @returns {IHttpPromise<T>} proxy for angular $http request promise
   *  with **deprecated** `success` and `error` convenience functions.
   * @see https://docs.angularjs.org/api/ng/service/$http
   */
  function request(serviceName, path, config = {}) {
    var promise = rnRegistry.getUrl(serviceName, path, true, config.version).then(function(url) {
      config.url = url;
      //$log.debug('Rhino requesting url ' + url);
      return $http(config);
    });

    return wrapHttpPromise(promise);
  } //request

  /**
   * @ngdoc function
   * @methodOf rhino.core.http.service:rnHttp
   * @description Proxy for an $http request based on a Genesis HAL link.
   * @param {object} link Genesis HAL link
   * @param {string} link.op HTTP method
   * @param {string} link.href HTTP URL
   * @param {*} data content to send in the request.  May be null.
   * @param {IRequestConfig} config additional $http config
   * @param {string} relativeToService service the path should be relative.  Should be null for absolute URLs.
   * @returns {IHttpPromise<T>} request promise
   */
  function halRequest(link, data, config, relativeToService) {
    if(!config) {
      config = {};
    }
    if(!config.headers) {
      config.headers = {};
    }
    // If there is no Content-Type specified and we are not told to allow undefined Content-Type, then provide a default. There are some
    // cases where the caller needs to explicitly pass an undefined Content-Type. For example, to tell the browser to add the
    // multipart/form-data type for you and automatically add the boundaries. If you set the Content-Type yourself to multipart/form-data
    // then the boundaries will not be filled in and your request will fail.
    if(!config.headers['Content-Type'] && !(config.hasOwnProperty('rhino') && config.rhino.allowUndefinedContentType)) {
      config.headers['Content-Type'] = 'application/json';
    }

    if(link.op) {
      config.method = link.op;
    } else {
      $log.warn('halRequest: HAL link missing "op" property for HTTP method. Modify the server API for consistency.');
    }
    if(link.href) {
      config.url = link.href;
    } else {
      $log.warn('halRequest: HAL link missing "href" property. Modify the server API for consistency.');
    }

    if(data) {
      config.data = data;
    }

    if(!relativeToService) {
      return wrapHttpPromise($http(config));
    } else {
      return request(relativeToService, link.href, config);
    }
  } //halRequest

  /**
   * Creates a shortcut function for a request, mirroring $http shortcut methods.
   *
   * @param httpMethod method name to create a shortcut for
   * @returns {Function}
   */
  function createShortcut(httpMethod) {
    return function(serviceName, path, config) {
      if(!config) {
        config = {};
      }
      config.method = httpMethod;

      return request(serviceName, path, config);
    };
  } //createShortcut

  /**
   * Creates a shortcut function for a request with a body, mirroring $http shortcut methods.
   *
   * @param httpMethod method name to create a shortcut for
   * @returns {Function}
   */
  function createShortcutWithContent(httpMethod) {
    return function(serviceName, path, data, config) {
      if(!config) {
        config = {};
      }
      config.data = data;
      config.method = httpMethod;
      // Our DELETE with data requires application/json content type
      if(!config.headers) {
        config.headers = {};
      }
      // If there is no Content-Type specified and we are not told to allow undefined Content-Type, then provide a default. There are some
      // cases where the caller needs to explicitly pass an undefined Content-Type. For example, to tell the browser to add the
      // multipart/form-data type for you and automatically add the boundaries. If you set the Content-Type yourself to multipart/form-data
      // then the boundaries will not be filled in and your request will fail.
      if(!config.headers['Content-Type'] && !(config.hasOwnProperty('rhino') && config.rhino.allowUndefinedContentType)) {
        config.headers['Content-Type'] = 'application/json';
      }

      return request(serviceName, path, config);
    };
  } //createShortcut

  /**
   * @ngdoc function
   * @name resolveWithData
   * @methodOf rhino.core.http.service:rnHttp
   * @description Simple function to use as a default handler to resolve a promise with response data.
   * @param {object} response http response
   * @returns {Promise<object>} a resolved promise with the response data
   *   or returns a rejected promise if response data is missing
   * @example
   * ```javascript
   *  rnHttp.get('some_service', 'some/path')
   *    .then(rnHttp.resolveWithData)
   *    .catch(rnHttp.rejectWithSmsError);
   * ```
   */
  function resolveWithData(response) {
    let data = _.get(response, 'data');
    if(data === undefined) {
      let errResponse = {
        data: {
          smsError: {
            message: 'resolveWithData() expected response data'
          }
        }
      };
      return $q.reject(errResponse);
    }

    return $q.resolve(data);
  }

  /**
   * @ngdoc function
   * @name rejectWithSmsError
   * @methodOf rhino.core.http.service:rnHttp
   * @description Simple function to use as a default error handler to reject a promise with the typical PrintOS SmsError.
   * @param {object} response http response
   * @returns {Promise<object>} a rejected promise with the SmsError
   *   or the response itself if the response does not have 'data.smsError'.
   * @example
   * ```javascript
   *  rnHttp.get('some_service', 'some/path')
   *    .then(rnHttp.resolveWithData)
   *    .catch(rnHttp.rejectWithSmsError);
   * ```
   */
  function rejectWithSmsError(response) {
    return $q.reject(_.get(response, 'data.smsError', response));
  }
} //rnHttp
